import {
  findConditionComparison,
  findConditionField,
  formatConditionValue,
  isConditionGroup,
} from '@/utils/conditions.js';

/**
 * Shared rendering logic for a single node of a condition tree (group or leaf),
 * reused by both ConditionSnapshot (evaluation view) and ConditionDefinition
 * (change-log diff view). It resolves the human-readable pieces — the field
 * label, operator, threshold/value, date period, subfield and dynamic operator
 * phrasing — so each component keeps its own distinct presentation.
 *
 * It is also self-sufficient: given the `adAccount`, it derives the currency
 * and lazily resolves dynamic subfield labels via the cached `dynamicSubfields`
 * store (so the host does not prop-drill `currency`/`subfieldLabels`).
 *
 * The host component MUST provide `node` (a prop or a computed — ConditionSnapshot
 * uses a prop, ConditionDefinition derives it from its item + side), plus
 * `fields`, `comparisons` and `adAccount`.
 */
export default {
  created() {
    this.ensureSubfieldsLoaded();
  },
  computed: {
    languages() {
      return this.$store.getters['app/languagesISO'];
    },
    isGroup() {
      return isConditionGroup(this.node);
    },
    logicClass() {
      return this.node.logic === 'AND' ? 'and' : 'or';
    },
    field() {
      return findConditionField(this.node.field, this.fields);
    },
    comparison() {
      return findConditionComparison(this.node.comparison, this.comparisons);
    },
    isDynamic() {
      return !!(this.comparison && this.comparison.dynamic_num);
    },
    fieldLabel() {
      return this.field ? this.tr(this.field.label) : this.node.field;
    },
    datePeriodLabel() {
      if (!this.node.date_period || !this.field || !Array.isArray(this.field.date_periods)) {
        return null;
      }

      const period = this.field.date_periods.find(item => item.machine_name === this.node.date_period);
      return period ? this.tr(period.label) : null;
    },
    subfieldLabel() {
      if (!this.node.subfield) {
        return null;
      }

      const adAccountId = this.adAccount?.id;
      const subfields = adAccountId
        ? this.$store.getters['dynamicSubfields/subfields'](this.node.field, adAccountId)
        : null;

      const match = (subfields || []).find(item => item.machine_name === this.node.subfield);
      return (match && match.label) || this.node.subfield;
    },
    currency() {
      return this.adAccount?.currencyDetails ?? null;
    },
    formatContext() {
      return {
        currency: this.currency,
        languages: this.languages,
      };
    },
    isTextField() {
      return !!this.field && this.field.data_type === 'string';
    },
    comparisonLabel() {
      return this.comparison ? this.tr(this.comparison.label) : this.node.comparison;
    },
    searchTerm() {
      return this.node.value != null ? String(this.node.value) : '';
    },
    requirementText() {
      if (this.isDynamic) {
        return this.dynamicRequirementText;
      }

      const operator = this.comparison ? this.tr(this.comparison.label) : this.node.comparison;
      const value = formatConditionValue(this.node.value, this.field, this.comparison, this.formatContext);

      return [ operator, value ].filter(part => part !== '' && part != null).join(' ');
    },
    dynamicRequirementText() {
      const settings = this.node.settings || {};
      const fieldType = (this.node.field || '').split('.')[0];

      const operator = this.comparison ? this.tr(this.comparison.label) : '';

      const directionOption = (this.comparison?.dynamic_value_options || [])
        .find(option => option.machine_name === settings.dynamic_operator_direction);
      const direction = directionOption ? this.tr(directionOption.label) : '%';

      const areaOptions = this.comparison?.areas ? this.comparison.areas[fieldType] : null;
      const areaOption = Array.isArray(areaOptions)
        ? areaOptions.find(option => option.machine_name === settings.dynamic_operator_area)
        : null;
      const area = areaOption ? this.tr(areaOption.label) : '';

      const value = this.node.value != null && this.node.value !== ''
        ? this.node.value + direction
        : '–';

      return [ operator, value, area ]
        .filter(part => part !== '' && part != null)
        .join(' ');
    },
  },
  methods: {
    formatConditionValue,
    // Lazily warm the dynamicSubfields cache for this leaf's field. The store
    // de-duplicates concurrent and repeat requests, so every leaf can ask
    // freely; subfieldLabel() then reads the resolved list reactively.
    ensureSubfieldsLoaded() {
      if (this.isGroup || !this.node.subfield || !this.adAccount?.id) {
        return;
      }

      this.$store.dispatch('dynamicSubfields/fetch', {
        adAccountId: this.adAccount.id,
      }).catch(() => { /* fall back to the machine name */ });
    },
  },
};
